import * as tus from "tus-js-client";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = (token) => (token ? { "X-Application-Token": token } : {});

async function req(path, { method = "GET", token, body } = {}) {
  const r = await fetch(`${API}${path}`, { method, credentials: "include", headers: { ...hdrs(token), ...(body ? { "Content-Type": "application/json" } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || "Something went wrong");
  return data;
}

export const videoConfig = () => req("/videos/config");
export const saveVideoLimits = (body) => req("/admin/video-settings", { method: "PUT", body });
export const saveCfCredentials = (body) => req("/admin/video-settings/credentials", { method: "POST", body });
export const verifyCf = () => req("/admin/video-settings/verify", { method: "POST" });
export const listVideos = (scope) => req(`/videos?${scope.type === "app" ? "application_id" : "horse_id"}=${scope.id}`, { token: scope.token });
export const videoPlayback = (id, token) => req(`/videos/${id}/playback`, { token });
export const deleteVideo = (id, token) => req(`/videos/${id}`, { method: "DELETE", token });
export const mediaDownloadStatus = (horseId, key) => req(`/horses/${horseId}/media-files/downloads${key ? `?key=${encodeURIComponent(key)}` : ""}`);
export const mediaDownloadUrl = (horseId, key) => `${API}/horses/${horseId}/media-files/${encodeURIComponent(key)}/download`;
export const mediaZipUrl = (horseId, scope) => `${API}/horses/${horseId}/media-files/download-all?scope=${scope}`;
const markUploaded = (id, token) => req(`/videos/${id}/uploaded`, { method: "POST", token });
const markFailed = (id, token, error) => req(`/videos/${id}/failed`, { method: "POST", token, body: { error } }).catch(() => {});
export const reportProgress = (id, token, progress) => req(`/videos/${id}/progress`, { method: "POST", token, body: { progress } }).catch(() => {});

const fpKey = (f) => `bahs-video:${f.name}|${f.size}|${f.lastModified}`;

// Resumable direct upload to Cloudflare (bytes never pass through our server).
// Our auth headers go ONLY to our backend: Cloudflare's CORS rejects unknown headers, which stalled uploads at 0%.
export function startUpload(scope, file, { retryId, onProgress, onVideoId, onDone, onError }) {
  let videoId = retryId || localStorage.getItem(fpKey(file)) || null;
  const up = new tus.Upload(file, {
    endpoint: `${API}/videos/tus/${scope.type}/${scope.id}`,
    chunkSize: 10 * 1024 * 1024,
    retryDelays: [0, 2000, 5000, 10000, 20000, 30000, 60000],
    onShouldRetry: (err) => {
      const s = err?.originalResponse?.getStatus?.() || 0;
      return s === 0 || s === 409 || s === 423 || s >= 500;
    },
    metadata: { name: file.name, filetype: file.type || "video/quicktime" },
    removeFingerprintOnSuccess: true,
    onBeforeRequest: (r) => {
      if (!r.getURL().startsWith(API)) return;
      r.getUnderlyingObject().withCredentials = true;
      if (scope.token) r.setHeader("X-Application-Token", scope.token);
      if (retryId) r.setHeader("X-Retry-Video-Id", retryId);
    },
    onAfterResponse: (r, res) => {
      if (!r.getURL().startsWith(API)) return;
      const id = res.getHeader("X-Video-Id");
      if (id) { videoId = id; localStorage.setItem(fpKey(file), id); onVideoId?.(id); }
    },
    onProgress: (sent, total) => onProgress(Math.round((sent / total) * 100), sent),
    onSuccess: async () => {
      localStorage.removeItem(fpKey(file));
      try { if (videoId) await markUploaded(videoId, scope.token); onDone(); } catch (e) { onError(e.message); }
    },
    onError: (e) => {
      const msg = e?.originalResponse?.getBody?.() ? (() => { try { return JSON.parse(e.originalResponse.getBody()).detail; } catch { return null; } })() : null;
      if (videoId && navigator.onLine) markFailed(videoId, scope.token, msg || "Upload interrupted");
      onError(msg || (navigator.onLine ? "Upload failed — check your connection and tap Retry." : "You're offline. The upload will continue when your connection returns."), !msg);
    },
  });
  // Always try to resume the same Cloudflare upload first (after a refresh or retry); otherwise start a new one.
  up.findPreviousUploads().then((p) => { if (p.length) up.resumeFromPreviousUpload(p[0]); up.start(); });
  return up;
}

export function readDuration(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (d) => { URL.revokeObjectURL(url); resolve(d); };
    const t = setTimeout(() => done(null), 3000);
    v.preload = "metadata";
    v.onloadedmetadata = () => { clearTimeout(t); done(Number.isFinite(v.duration) ? v.duration : null); };
    v.onerror = () => { clearTimeout(t); done(null); };
    v.src = url;
  });
}

export const STATUS = {
  waiting: ["Preparing", "bg-stone-100 text-stone-700"],
  uploading: ["Uploading video", "bg-sky-100 text-sky-800"],
  stalled: ["Upload stalled", "bg-orange-100 text-orange-900"],
  processing: ["Processing video", "bg-amber-100 text-amber-900"],
  ready: ["Video Ready", "bg-emerald-100 text-emerald-800"],
  failed: ["Upload Failed", "bg-red-100 text-red-700"],
};
